import { useState, useEffect } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useUpdateEntryRule } from '../../api/useEntry';
import { EntryRule } from '../../types/entry.types';

interface Props {
  open: boolean;
  onClose: () => void;
  rule: EntryRule;
}

/** Canonical engine defaults for the sweep dual-post / adverse-cancel knobs. */
const SWEEP_DEFAULTS = {
  sweepWindowMs: 400,
  sweepMinTicks: 2,
  sweepMinLevelsCleared: 2,
  sweepDepthFraction: 0.5,
  sweepMinContractsFloor: 10,
  sweepDeltaSigma: 3.0,
  sweepConfirmMs: 0, // persistence/cancel filter OFF by default; > 0 re-enables it

  sweepFreshMs: 500,
  sweepResolveMs: 750,
  sweepKalshiOffsetLevels: 1,
  sweepPolymarketOffsetLevels: 1,
  adverseCancelTicks: 2,
  liveMakerSizeFactor: 1.0,
} as const;

/**
 * Compact editor for an entry rule's sweep dual-post + adverse-cancel config.
 * The opener page is an inline-cell table with no per-rule settings form, so
 * this small dialog owns the sweep fields and saves them via
 * `useUpdateEntryRule`. Mirrors `HedgeCheckEditorDialog`'s reveal-when-checked
 * layout (`{enabled && (<Stack>...inputs...</Stack>)}`).
 */
export const SweepDualPostEditorDialog = ({ open, onClose, rule }: Props) => {
  const updateMutation = useUpdateEntryRule();

  const [sweepEnabled, setSweepEnabled] = useState(false);
  const [windowMs, setWindowMs] = useState('');
  const [minTicks, setMinTicks] = useState('');
  const [minLevelsCleared, setMinLevelsCleared] = useState('');
  const [depthFraction, setDepthFraction] = useState('');
  const [minContractsFloor, setMinContractsFloor] = useState('');
  const [deltaSigma, setDeltaSigma] = useState('');
  const [confirmMs, setConfirmMs] = useState('');
  const [freshMs, setFreshMs] = useState('');
  const [resolveMs, setResolveMs] = useState('');
  const [kalshiOffsetLevels, setKalshiOffsetLevels] = useState('');
  const [polymarketOffsetLevels, setPolymarketOffsetLevels] = useState('');

  const [adverseEnabled, setAdverseEnabled] = useState(true);
  const [adverseTicks, setAdverseTicks] = useState('');
  const [makerSizeFactor, setMakerSizeFactor] = useState('');

  useEffect(() => {
    if (!open) return;
    setSweepEnabled(!!rule.sweepDualPostEnabled);
    setWindowMs(String(rule.sweepWindowMs ?? SWEEP_DEFAULTS.sweepWindowMs));
    setMinTicks(String(rule.sweepMinTicks ?? SWEEP_DEFAULTS.sweepMinTicks));
    setMinLevelsCleared(
      String(rule.sweepMinLevelsCleared ?? SWEEP_DEFAULTS.sweepMinLevelsCleared),
    );
    setDepthFraction(
      String(rule.sweepDepthFraction ?? SWEEP_DEFAULTS.sweepDepthFraction),
    );
    setMinContractsFloor(
      String(
        rule.sweepMinContractsFloor ?? SWEEP_DEFAULTS.sweepMinContractsFloor,
      ),
    );
    setDeltaSigma(String(rule.sweepDeltaSigma ?? SWEEP_DEFAULTS.sweepDeltaSigma));
    setConfirmMs(String(rule.sweepConfirmMs ?? SWEEP_DEFAULTS.sweepConfirmMs));
    setFreshMs(String(rule.sweepFreshMs ?? SWEEP_DEFAULTS.sweepFreshMs));
    setResolveMs(String(rule.sweepResolveMs ?? SWEEP_DEFAULTS.sweepResolveMs));
    setKalshiOffsetLevels(
      String(
        rule.sweepKalshiOffsetLevels ?? SWEEP_DEFAULTS.sweepKalshiOffsetLevels,
      ),
    );
    setPolymarketOffsetLevels(
      String(
        rule.sweepPolymarketOffsetLevels ??
          SWEEP_DEFAULTS.sweepPolymarketOffsetLevels,
      ),
    );

    setAdverseEnabled(rule.adverseCancelEnabled ?? true);
    setAdverseTicks(
      String(rule.adverseCancelTicks ?? SWEEP_DEFAULTS.adverseCancelTicks),
    );
    setMakerSizeFactor(
      String(rule.liveMakerSizeFactor ?? SWEEP_DEFAULTS.liveMakerSizeFactor),
    );
  }, [open, rule]);

  const handleSave = async () => {
    await updateMutation.mutateAsync({
      id: rule._id,
      dto: {
        sweepDualPostEnabled: sweepEnabled,
        ...(sweepEnabled
          ? {
              sweepWindowMs: Number(windowMs),
              sweepMinTicks: Number(minTicks),
              sweepMinLevelsCleared: Number(minLevelsCleared),
              sweepDepthFraction: Number(depthFraction),
              sweepMinContractsFloor: Number(minContractsFloor),
              sweepDeltaSigma: Number(deltaSigma),
              sweepConfirmMs: Number(confirmMs),
              sweepFreshMs: Number(freshMs),
              sweepResolveMs: Number(resolveMs),
              sweepKalshiOffsetLevels: Number(kalshiOffsetLevels),
              sweepPolymarketOffsetLevels: Number(polymarketOffsetLevels),
            }
          : {}),
        adverseCancelEnabled: adverseEnabled,
        ...(adverseEnabled
          ? {
              adverseCancelTicks: Number(adverseTicks),
              liveMakerSizeFactor: Number(makerSizeFactor),
            }
          : {}),
      },
    });
    onClose();
  };

  const numField = (
    label: string,
    value: string,
    setter: (v: string) => void,
    step: number,
    min = 0,
  ) => (
    <TextField
      label={label}
      type="number"
      size="small"
      value={value}
      onChange={e => setter(e.target.value)}
      inputProps={{ step, min }}
      fullWidth
    />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: '1rem' }}>
        Sweep dual-post — {rule.pairName}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography sx={{ fontSize: '0.75rem', color: 'grey.500' }}>
            When a large adverse book sweep is detected, dual-post both legs to
            re-capture the move. Adverse cancel pulls the resting maker when the
            book moves against us.
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={sweepEnabled}
                onChange={e => setSweepEnabled(e.target.checked)}
                size="small"
                color="warning"
              />
            }
            label={
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                Sweep dual-post
              </Typography>
            }
          />
          {sweepEnabled && (
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={2}>
                {numField('Sweep window (ms)', windowMs, setWindowMs, 50)}
                {numField('Min ticks', minTicks, setMinTicks, 1)}
              </Stack>
              <Stack direction="row" spacing={2}>
                {numField(
                  'Min levels cleared',
                  minLevelsCleared,
                  setMinLevelsCleared,
                  1,
                )}
                {numField('Depth fraction', depthFraction, setDepthFraction, 0.1)}
              </Stack>
              <Stack direction="row" spacing={2}>
                {numField(
                  'Min contracts floor',
                  minContractsFloor,
                  setMinContractsFloor,
                  1,
                )}
                {numField('Delta sigma', deltaSigma, setDeltaSigma, 0.5)}
              </Stack>
              <Stack direction="row" spacing={2}>
                {numField('Confirm (ms)', confirmMs, setConfirmMs, 50)}
                {numField('Fresh (ms)', freshMs, setFreshMs, 50)}
              </Stack>
              <Stack direction="row" spacing={2}>
                {numField('Resolve (ms)', resolveMs, setResolveMs, 50)}
                {numField(
                  'Kalshi offset levels',
                  kalshiOffsetLevels,
                  setKalshiOffsetLevels,
                  1,
                )}
              </Stack>
              <Stack direction="row" spacing={2}>
                {numField(
                  'Polymarket offset levels',
                  polymarketOffsetLevels,
                  setPolymarketOffsetLevels,
                  1,
                )}
                <TextField sx={{ visibility: 'hidden' }} size="small" fullWidth />
              </Stack>
            </Stack>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={adverseEnabled}
                onChange={e => setAdverseEnabled(e.target.checked)}
                size="small"
                color="warning"
              />
            }
            label={
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                Adverse cancel (recommended)
              </Typography>
            }
          />
          {adverseEnabled && (
            <Stack direction="row" spacing={2}>
              {numField(
                'Adverse cancel ticks',
                adverseTicks,
                setAdverseTicks,
                1,
              )}
              {numField(
                'Live maker size factor',
                makerSizeFactor,
                setMakerSizeFactor,
                0.1,
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} size="small">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={updateMutation.isLoading}
          size="small"
        >
          {updateMutation.isLoading ? <CircularProgress size={16} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
